import React, { useId } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { colors, headingFont, uiFont } from '../theme';
import {
  getMediaTypeLabel,
  getProductConditionLabel,
  getProductDisplayName,
  getProductImageSrcSet,
  getProductImageUrl,
  getProductMediaType,
  getProductPriceLabel,
  getProductStatusLabel,
  isProductAvailable,
} from '../utils/product';

const sectionLabelSx = {
  fontFamily: uiFont, fontSize: '0.72rem', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.secondary, mb: 1.25,
};

function ProductSection({ title, children, sx }) {
  const id = useId();
  return (
    <Box component="section" aria-labelledby={id} sx={{
      minWidth: 0, p: { xs: 1.5, sm: 2 }, border: `1px solid ${colors.border}`,
      backgroundColor: colors.page, borderRadius: 1, ...sx,
    }}>
      <Typography component="h3" id={id} sx={sectionLabelSx}>{title}</Typography>
      {children}
    </Box>
  );
}

// Shared presentation only: callers retain their own cart, links and playback handlers.
function ProductPresentation({ product, actions, previews, titleComponent = 'h2', imagePriority = false, showEmptySections = false }) {
  const mediaType = getMediaTypeLabel(getProductMediaType(product));
  const title = getProductDisplayName(product);
  const inStock = isProductAvailable(product);
  const metadata = [
    ['Label', product.label], ['Estilo', product.style],
    ['Formato', product.format], ['Pais', product.country],
    ['Año', product.year], ['Catalogo', product.catalogNumber],
  ];
  const readingSx = { whiteSpace: 'pre-line', overflowWrap: 'anywhere', color: colors.secondary, lineHeight: 1.65 };

  return (
    <Stack spacing={{ xs: 2.5, sm: 3 }}>
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.8fr) minmax(0, 1.2fr)' },
        alignItems: 'start', gap: { xs: 2, md: 3 },
      }}>
        <Box component="img" src={getProductImageUrl(product.image, 'detail')}
          srcSet={getProductImageSrcSet(product.image, 'detail')}
          sizes="(min-width: 900px) 360px, (min-width: 600px) 340px, 280px"
          alt={title} width={360} height={360}
          loading={imagePriority ? 'eager' : 'lazy'} fetchPriority={imagePriority ? 'high' : undefined}
          decoding="async" sx={{
            display: 'block', width: '100%', height: 'auto', aspectRatio: '1 / 1',
            maxWidth: { xs: 280, sm: 340, md: 360 }, mx: 'auto', objectFit: 'contain',
            backgroundColor: colors.page, border: `1px solid ${colors.border}`, borderRadius: 1,
          }} />

        <Stack spacing={1.75} sx={{ minWidth: 0 }}>
          <Box>
            <Typography variant="caption" sx={{ color: colors.accent, display: 'block', mb: 0.75 }}>
              {product.artist || 'Artista sin especificar'}
            </Typography>
            <Typography component={titleComponent} sx={{
              fontFamily: headingFont, fontWeight: 400, textTransform: 'uppercase',
              fontSize: { xs: '1.9rem', sm: '2.3rem', md: '2.6rem' }, lineHeight: 1.12,
              letterSpacing: '0.015em', overflowWrap: 'anywhere', color: colors.text,
            }}>{product.title || product.name || title}</Typography>
          </Box>

          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={mediaType} variant="outlined" />
            <Chip size="small" label={getProductConditionLabel(product)} variant="outlined" />
            <Chip size="small" label={getProductStatusLabel(product)} sx={{
              color: inStock ? colors.accent : colors.secondary, backgroundColor: inStock ? colors.tint : colors.button,
            }} />
          </Stack>
          <Typography sx={{ fontFamily: headingFont, fontSize: '1.8rem', lineHeight: 1.1, color: colors.text }}>
            {getProductPriceLabel(product)}
          </Typography>

          <Box component="dl" sx={{
            m: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '10px 16px', borderTop: `1px solid ${colors.border}`, pt: 1.5,
          }}>
            {metadata.map(([label, value]) => (
              <Box key={label} sx={{ minWidth: 0 }}>
                <Typography component="dt" sx={{ fontFamily: uiFont, fontSize: '0.62rem', textTransform: 'uppercase', color: colors.muted }}>
                  {label}
                </Typography>
                <Typography component="dd" sx={{ m: 0, mt: 0.2, fontSize: '0.85rem', overflowWrap: 'anywhere' }}>
                  {value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.5,
            '& .MuiButton-root': { textTransform: 'uppercase', flex: { xs: '1 1 100%', sm: '1 1 auto' }, minHeight: 44, fontSize: '0.7rem' },
          }}>{actions}</Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' }}>
        {(product.tracklist || product.description || showEmptySections) && <Stack spacing={2} sx={{ minWidth: 0 }}>
          {(product.tracklist || showEmptySections) && <ProductSection title="Tracklist">
            <Typography variant="body2" sx={readingSx}>{product.tracklist || 'Sin tracklist'}</Typography>
          </ProductSection>}
          {(product.description || showEmptySections) && <ProductSection title="Descripcion">
            <Typography variant="body2" sx={readingSx}>{product.description || 'Sin descripción'}</Typography>
          </ProductSection>}
        </Stack>}
        <ProductSection title="Previews" sx={{ order: { xs: -1, md: 0 } }}>{previews}</ProductSection>
      </Box>
    </Stack>
  );
}

export default ProductPresentation;
